import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import Panel from "../components/brand/Panel";
import Badge from "../components/brand/Badge";
import KpiTile from "../components/brand/KpiTile";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  TableSortLabel,
  Button,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import { getComplianceStats, getAccessibleProperties } from "../api/dashboard";
import { fonts } from "../theme/tokens";

const typeTone = (t) => {
  if (t === "Insurance") return "valid";
  if (t === "Assessment") return "Medium";
  if (t === "Utility") return "In Progress";
  return "neutral";
};

export default function RenewalsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [complianceData, setComplianceData] = useState({
    expired_items: [],
    expiring_soon_items: [],
  });
  const [properties, setProperties] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [liveFilter, setLiveFilter] = useState("live");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortField, setSortField] = useState("days_remaining");
  const [sortDirection, setSortDirection] = useState("asc");

  const tabParam = searchParams.get("tab");
  const activeTab = tabParam === "expired" ? 1 : 0;

  const goTab = useCallback(
    (key) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", key);
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const apiLiveFilter = liveFilter === "all" ? null : liveFilter === "live";
        const [complianceRes, propertiesRes] = await Promise.all([
          getComplianceStats(apiLiveFilter),
          getAccessibleProperties(apiLiveFilter),
        ]);

        setComplianceData(complianceRes || { expired_items: [], expiring_soon_items: [] });

        const props = propertiesRes?.data || [];
        const uniqueProps = [...new Map(props.map((p) => [p.property_id, p])).values()];
        setProperties(uniqueProps);
      } catch (e) {
        console.error("Failed to load data:", e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [liveFilter]);

  const { expired_items, expiring_soon_items } = complianceData;

  const displayItems = useMemo(() => {
    let items = activeTab === 0 ? [...expiring_soon_items] : [...expired_items];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.name?.toLowerCase().includes(query) ||
          item.property_name?.toLowerCase().includes(query) ||
          item.unit_name?.toLowerCase().includes(query) ||
          item.unit_code?.toLowerCase().includes(query) ||
          item.type?.toLowerCase().includes(query)
      );
    }

    if (propertyFilter !== "all") {
      items = items.filter((item) => item.property_id === parseInt(propertyFilter, 10));
    }

    if (typeFilter !== "all") {
      items = items.filter((item) => item.type === typeFilter);
    }

    items.sort((a, b) => {
      let aVal;
      let bVal;

      switch (sortField) {
        case "days_remaining":
          aVal = a.days_remaining;
          bVal = b.days_remaining;
          break;
        case "expiry_date":
          aVal = new Date(a.expiry_date).getTime();
          bVal = new Date(b.expiry_date).getTime();
          break;
        case "name":
          aVal = a.name?.toLowerCase() || "";
          bVal = b.name?.toLowerCase() || "";
          break;
        case "type":
          aVal = a.type?.toLowerCase() || "";
          bVal = b.type?.toLowerCase() || "";
          break;
        case "property":
          aVal = (a.property_name || a.unit_name || a.unit_code || "")?.toLowerCase();
          bVal = (b.property_name || b.unit_name || b.unit_code || "")?.toLowerCase();
          break;
        default:
          aVal = a.days_remaining;
          bVal = b.days_remaining;
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });

    return items;
  }, [
    activeTab,
    expired_items,
    expiring_soon_items,
    searchQuery,
    propertyFilter,
    typeFilter,
    sortField,
    sortDirection,
  ]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setLiveFilter("live");
    setPropertyFilter("all");
    setTypeFilter("all");
    setSortField("days_remaining");
    setSortDirection("asc");
  };

  const hasActiveFilters =
    searchQuery ||
    liveFilter !== "live" ||
    propertyFilter !== "all" ||
    typeFilter !== "all";

  const handleRowClick = (item) => {
    const parentPath = item.property_id ? `/properties/${item.property_id}` : `/units/${item.unit_id}`;

    const typePath =
      {
        Assessment: "assessments",
        Insurance: "insurance",
        Utility: "utilities",
      }[item.type] || "details";

    navigate(`${parentPath}/${typePath}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const urgencyCell = (daysRemaining, isExpiredTab) => {
    if (isExpiredTab || daysRemaining < 0) {
      const overdueDays = Math.abs(daysRemaining);
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <ErrorOutlineIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: theme.palette.error.main }}>
            {overdueDays} day{overdueDays === 1 ? "" : "s"} overdue
          </Typography>
        </Box>
      );
    }
    if (daysRemaining <= 30) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: theme.palette.error.main }}>
            {daysRemaining} day{daysRemaining === 1 ? "" : "s"} left
          </Typography>
        </Box>
      );
    }
    if (daysRemaining <= 60) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 18, color: theme.palette.brand.goldDeep }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: theme.palette.brand.goldDeep }}>
            {daysRemaining} days left
          </Typography>
        </Box>
      );
    }
    return (
      <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: theme.palette.brand.inkMuted }}>
        {daysRemaining} days left
      </Typography>
    );
  };

  const selectFieldSx = {
    minWidth: 160,
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      fontSize: 12.5,
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiInputLabel-root": { fontSize: 12.5 },
  };

  const sortLabelSx = {
    "&.Mui-active": { color: theme.palette.brand.ink },
    "& .MuiTableSortLabel-icon": { color: `${theme.palette.brand.goldDeep} !important` },
  };

  const headCellSx = {
    color: theme.palette.brand.inkSoft,
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    fontFamily: fonts.mono,
    borderBottom: `1px solid ${theme.palette.brand.border}`,
    py: 1.5,
    px: 2.25,
  };

  return (
    <Layout eyebrow="Compliance · Portfolio" title="Renewals">
      <Typography sx={{ mb: 2.5, fontSize: 13, color: theme.palette.brand.inkMuted, maxWidth: 640 }}>
        Expiring and expired assessments, insurance, and utilities across your portfolio.
      </Typography>

      {/* KPI strip — matches dashboard compliance tiles */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.5,
          mb: 3,
        }}
      >
        <KpiTile
          label="Expiring soon"
          value={loading ? "—" : expiring_soon_items.length}
          sub="Next 30 days"
          tone="warning"
          onClick={() => goTab("expiring")}
          active={activeTab === 0}
        />
        <KpiTile
          label="Expired"
          value={loading ? "—" : expired_items.length}
          sub="Needs action"
          tone="danger"
          onClick={() => goTab("expired")}
          active={activeTab === 1}
        />
      </Box>

      {/* Filters — brand panel strip */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          mb: 2,
          p: 2,
          borderRadius: "8px",
          border: `1px solid ${theme.palette.brand.border}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <FilterListIcon sx={{ color: theme.palette.brand.inkSoft, fontSize: 20 }} />

        <TextField
          size="small"
          placeholder="Search documents…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            minWidth: 200,
            flex: "1 1 200px",
            "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: 12.5 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.brand.inkSoft, fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery("")} aria-label="Clear search">
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <FormControl size="small" sx={selectFieldSx}>
          <InputLabel>Portfolio</InputLabel>
          <Select
            value={liveFilter}
            label="Portfolio"
            onChange={(e) => {
              setLiveFilter(e.target.value);
              setPropertyFilter("all");
            }}
          >
            <MenuItem value="all">All properties</MenuItem>
            <MenuItem value="live">Live only</MenuItem>
            <MenuItem value="non-live">Non-live only</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ ...selectFieldSx, minWidth: 180 }}>
          <InputLabel>Property</InputLabel>
          <Select value={propertyFilter} label="Property" onChange={(e) => setPropertyFilter(e.target.value)}>
            <MenuItem value="all">All properties</MenuItem>
            {properties.map((p) => (
              <MenuItem key={p.property_id} value={p.property_id}>
                {p.property_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ ...selectFieldSx, minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="all">All types</MenuItem>
            <MenuItem value="Assessment">Assessment</MenuItem>
            <MenuItem value="Insurance">Insurance</MenuItem>
            <MenuItem value="Utility">Utility</MenuItem>
          </Select>
        </FormControl>

        {hasActiveFilters && (
          <Button size="small" variant="outlined" onClick={clearFilters} sx={{ fontSize: 12.5, borderRadius: "6px" }}>
            Clear filters
          </Button>
        )}
      </Box>

      <Panel
        eyebrow={activeTab === 0 ? "Expiring" : "Expired"}
        title={
          loading
            ? "Loading…"
            : hasActiveFilters
              ? `${displayItems.length} of ${activeTab === 0 ? expiring_soon_items.length : expired_items.length} documents`
              : `${displayItems.length} documents`
        }
      >
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>
                  <TableSortLabel
                    active={sortField === "name"}
                    direction={sortField === "name" ? sortDirection : "asc"}
                    onClick={() => handleSort("name")}
                    sx={sortLabelSx}
                  >
                    Document
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headCellSx}>
                  <TableSortLabel
                    active={sortField === "type"}
                    direction={sortField === "type" ? sortDirection : "asc"}
                    onClick={() => handleSort("type")}
                    sx={sortLabelSx}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headCellSx}>
                  <TableSortLabel
                    active={sortField === "property"}
                    direction={sortField === "property" ? sortDirection : "asc"}
                    onClick={() => handleSort("property")}
                    sx={sortLabelSx}
                  >
                    Property / unit
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headCellSx}>
                  <TableSortLabel
                    active={sortField === "expiry_date"}
                    direction={sortField === "expiry_date" ? sortDirection : "asc"}
                    onClick={() => handleSort("expiry_date")}
                    sx={sortLabelSx}
                  >
                    {activeTab === 0 ? "Expiry date" : "Expired on"}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headCellSx}>
                  <TableSortLabel
                    active={sortField === "days_remaining"}
                    direction={sortField === "days_remaining" ? sortDirection : "asc"}
                    onClick={() => handleSort("days_remaining")}
                    sx={sortLabelSx}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ px: 2.25, py: 1.5, borderColor: theme.palette.brand.border }}>
                      <Skeleton />
                    </TableCell>
                    <TableCell sx={{ borderColor: theme.palette.brand.border }}>
                      <Skeleton width={80} />
                    </TableCell>
                    <TableCell sx={{ borderColor: theme.palette.brand.border }}>
                      <Skeleton />
                    </TableCell>
                    <TableCell sx={{ borderColor: theme.palette.brand.border }}>
                      <Skeleton width={100} />
                    </TableCell>
                    <TableCell sx={{ borderColor: theme.palette.brand.border }}>
                      <Skeleton width={120} />
                    </TableCell>
                  </TableRow>
                ))
              ) : displayItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, border: "none" }}>
                    <Typography sx={{ color: theme.palette.brand.inkSoft, fontSize: 13 }}>
                      {hasActiveFilters
                        ? "No documents match your filters."
                        : activeTab === 0
                          ? "No documents expiring soon."
                          : "No expired documents."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayItems.map((item, index) => (
                  <TableRow
                    key={`${item.type}-${item.id}-${index}`}
                    onClick={() => handleRowClick(item)}
                    sx={{
                      cursor: "pointer",
                      transition: "background .12s",
                      "&:hover": { backgroundColor: theme.palette.brand.surfaceMuted },
                      "& td": {
                        borderBottom: `1px solid ${theme.palette.brand.border}`,
                        py: 1.75,
                        px: 2.25,
                        fontSize: 13,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: theme.palette.brand.ink, fontSize: 14 }}>
                        {item.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.type ? <Badge tone={typeTone(item.type)}>{item.type}</Badge> : "—"}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: theme.palette.brand.inkMuted, fontSize: 12.5 }}>
                        {item.property_name || item.unit_name || item.unit_code || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: theme.palette.brand.inkMuted, fontSize: 12.5 }}>
                        {formatDate(item.expiry_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>{urgencyCell(item.days_remaining, activeTab === 1)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
    </Layout>
  );
}
